import React, { useEffect, useRef, useState } from "react";
import * as yup from "yup";
import BaseModal from "../../../components/ui/BaseModal";
import BaseButton from "../../../components/ui/BaseButton";
import BaseAutoSelect from "../../../components/ui/BaseAutoSelect";
import BaseTextField from "../../../components/ui/BaseTextField";
import BaseEditor from "../../../components/ui/BaseEditor";
import BaseCheckbox from "../../../components/ui/BaseCheckbox";
import BaseDatePicker from "../../../components/ui/BaseDatePicker";
import BaseAsyncCitySelect from "../../../components/ui/BaseAsyncCitySelect";
import { useCountries } from "../../../hooks/useCountries";
import { useMutation } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useFormik } from "formik";
import {
  createEmployment,
  updateEmploymentHistory,
} from "../../../services/api/employment";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";
const validationSchema = yup.object({
  companyName: yup
    .string("Enter Institution Name")
    .required("Company Name is required"),
  designation: yup
    .string("Enter designation")
    .required("Designation is required"),
  country: yup.string().required("Country is required"),
  city: yup.string().required("city is required"),
  startDate: yup.string().required("start date is required"),
  endDate: yup.string().notRequired("end date is required"),
});

const AddEmpolymentHistoryModal = ({
  open,
  close,
  setEmploymentHistoryList,
  selectedEmployment,
  setSelectedEmployment,
  isEdit,
  targetUserId,
}) => {
  const dispatch = useDispatch();
  const { countries, getCountryLabel } = useCountries();

  const {
    isPending: isCreateEmploymentApiLoading,
    error: isCreateEmploymentApiError,
    mutateAsync: createEmploymentMutation,
  } = useMutation({
    mutationFn: (payload) => createEmployment(payload, targetUserId),
    enabled: false,
  });

  const {
    isPending: isUpdateEmploymentApiLoading,
    error: isUpdateEmploymentApiError,
    mutateAsync: updateEmploymentHistoryMutation,
  } = useMutation({
    mutationFn: updateEmploymentHistory,
    enabled: false,
  });

  const formik = useFormik({
    initialValues: {
      companyName: selectedEmployment?.companyName,
      designation: selectedEmployment?.designation,
      country: selectedEmployment?.country,
      city: selectedEmployment?.city,
      startDate: selectedEmployment?.startDate || null,
      endDate: selectedEmployment?.endDate || null,
      description: selectedEmployment?.description,
      isCurrentlyEmployed: !selectedEmployment?.endDate
        ? true
        : selectedEmployment?.isCurrentlyEmployed || false,
    },
    validationSchema: validationSchema,
    onSubmit: (values, { resetForm }) => {
      handleEmploymentSubmit();
    },
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
  });

  const handleAddEmployment = async () => {
    try {
      const { isCurrentlyEmployed, ...rest } = formik.values;
      const employmentResponse = await createEmploymentMutation(rest);
      dispatch(
        showToast({
          message: "Employment added successfully",
          type: "success",
        }),
      );
      setEmploymentHistoryList((prevEmployment) => [
        ...prevEmployment,
        employmentResponse,
      ]);
    } catch (error) {
      console.log(error);
      dispatch(
        showToast({
          message: error?.response?.data?.message,
          type: "error",
        }),
      );
    }
  };

  const handleEditEmployment = async () => {
    try {
      const { isCurrentlyEmployed, ...rest } = formik.values;
      const employmentResponse = await updateEmploymentHistoryMutation({
        ...rest,
        id: selectedEmployment.id,
      });
      setEmploymentHistoryList((prev) =>
        prev.map((item) =>
          item.id === selectedEmployment.id ? employmentResponse : item,
        ),
      );
      dispatch(
        showToast({
          message: "Employment updated successfully",
          type: "success",
        }),
      );
    } catch (error) {
      console.log(error);
      dispatch(
        showToast({
          message: error?.response?.data?.message,
          type: "error",
        }),
      );
    }
  };

  const handleEmploymentSubmit = () => {
    if (isEdit) {
      handleEditEmployment();
    } else {
      handleAddEmployment();
    }
    close(false);
    setSelectedEmployment(null);
    formik.resetForm();
  };

  const handleCountryChange = (option) => {
    formik.setFieldValue("country", option?.target?.value || "");
    // Cities are scoped to the country, so an existing pick is no longer valid.
    formik.setFieldValue("city", "");
  };

  const handleCitySelect = (city) => {
    formik.setFieldValue("city", city || "");
  };
  return (
    <BaseModal
      className="-top-[200px] overflow-auto"
      open={open}
      close={() => {
        close(false);
        setSelectedEmployment(null);
        formik.resetForm();
      }}
    >
      <div className="py-7 px-5 sm:px-12 w-full lg:w-[900px] xl:w-[1000px]">
        <h6 className="text-dark font-normal border-b pb-1">
          Employement History
        </h6>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-2 mt-4 [&>*]:min-w-0">
          <BaseTextField
            label="Company Name"
            isDefaultField={false}
            labelShrink={false}
            optional={false}
            id="companyName"
            name="companyName"
            value={formik.values.companyName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.companyName && Boolean(formik.errors.companyName)
            }
            helperText={formik.touched.companyName && formik.errors.companyName}
          />

          <BaseTextField
            label="Designation"
            isDefaultField={false}
            labelShrink={false}
            optional={false}
            id="designation"
            name="designation"
            value={formik.values.designation}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.designation && Boolean(formik.errors.designation)
            }
            helperText={formik.touched.designation && formik.errors.designation}
          />

          <BaseAutoSelect
            labelShrink={false}
            label="Country"
            placeholder="Select Country"
            required={true}
            isDefaultField={false}
            id="country"
            name="country"
            options={countries || []}
            value={formik.values.country || ""}
            onChange={handleCountryChange}
            onBlur={formik.handleBlur}
            error={formik.touched.country && Boolean(formik.errors.country)}
            helperText={formik.touched.country && formik.errors.country}
          />
          <div>
            <BaseAsyncCitySelect
              required={true}
              labelShrink={false}
              label="City"
              placeholder="Enter City"
              country={
                formik.values.country ? getCountryLabel(formik.values.country) : ""
              }
              value={formik.values.city || ""}
              onChange={handleCitySelect}
            />
            {formik.touched.city && formik.errors.city && (
              <p className="text-[#d32f2f] text-xs">{formik.errors.city}</p>
            )}
          </div>
          <div>
            <p className="font-normal">Start Date</p>
            <BaseDatePicker
              className="border-none"
              type="date"
              id="startDate"
              name="startDate"
              value={dayjs(formik.values.startDate)}
              onChange={(event) => {
                const selectedDate = dayjs(event)
                  .utcOffset(0, true)
                  .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
                formik.setFieldValue("startDate", selectedDate); // Store as string
              }}
              onBlur={formik.handleBlur}
              error={
                formik.touched.startDate && Boolean(formik.errors.startDate)
              }
              helperText={formik.touched.startDate && formik.errors.startDate}
              slotProps={{
                textField: {
                  sx: {
                    "& .MuiOutlinedInput-root": {
                      border: "none !important",
                      boxShadow: "1px 1px 4px 1px #00000021",
                      borderRadius: "5px",
                      backgroundColor: "white",
                      height: "40px",
                      display: "flex",
                      alignItems: "center",
                      margin: "8px 1.5px",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none !important",
                    },
                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                      {
                        border: "none !important",
                        boxShadow: "none",
                      },
                    "& .MuiInputBase-input": {
                      padding: "10px 14px",
                      fontSize: "13px",
                      height: "100% !important",
                      lineHeight: "1.5",
                    },

                    "& .MuiIconButton-root": {
                      backgroundColor: "white !important",
                      borderLeft: "none !important",
                    },
                  },
                },
              }}
            />
          </div>
          <div>
            <p
              className={`font-normal ${
                formik.values.isCurrentlyEmployed ? "text-gray-400" : ""
              }`}
            >
              End Date
            </p>
            <BaseDatePicker
              disabled={formik.values.isCurrentlyEmployed}
              className="border-none"
              type="date"
              id="endDate"
              name="endDate"
              minDate={
                formik.values.startDate
                  ? dayjs(formik.values.startDate)
                  : undefined
              }
              value={dayjs(formik.values.endDate)}
              onChange={(event) => {
                const selectedDate = dayjs(event)
                  .utcOffset(0, true)
                  .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
                formik.setFieldValue("endDate", selectedDate); // Store as string
              }}
              onBlur={formik.handleBlur}
              error={formik.touched.endDate && Boolean(formik.errors.endDate)}
              helperText={formik.touched.endDate && formik.errors.endDate}
              slotProps={{
                textField: {
                  sx: {
                    "& .MuiOutlinedInput-root": {
                      border: "none !important",
                      boxShadow: "1px 1px 4px 1px #00000021",
                      borderRadius: "5px",
                      backgroundColor: "white",
                      height: "40px",
                      display: "flex",
                      alignItems: "center",
                      margin: "8px 1.5px",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none !important",
                    },
                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                      {
                        border: "none !important",
                        boxShadow: "none",
                      },
                    "& .MuiInputBase-input": {
                      padding: "10px 14px",
                      fontSize: "13px",
                      height: "100% !important",
                      lineHeight: "1.5",
                    },

                    "& .MuiIconButton-root": {
                      backgroundColor: "white !important",
                      borderLeft: "none !important",
                    },
                  },
                },
              }}
            />
          </div>
          <BaseCheckbox
            label="Currently Employed"
            checked={formik.values.isCurrentlyEmployed}
            onChange={(e) => {
              formik.setFieldValue("isCurrentlyEmployed", e.target.checked);

              if (e.target.checked) {
                formik.setFieldValue("endDate", null);
              }
            }}
          />
          <div className="col-span-1 lg:col-span-2 mt-4">
            <p className="font-normal mb-2">Description</p>
            <BaseEditor
              value={formik.values.description || ""}
              onChange={(val) => formik.setFieldValue("description", val)}
              showToolbar={true}
              className="rounded-md"
            />
            {formik.touched.description && formik.errors.description && (
              <p className="text-red-500 text-xs mt-1">{formik.errors.description}</p>
            )}
          </div>
        </div>
      </div>

      <div
        className="d-flex content-center bg-white py-6 mt-6"
        style={{
          boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)",
        }}
      >
        <BaseButton
          className=" px-[28px] py-[5px] bg-light text-primary hover:text-primary"
          variant="outlined"
          color="primary"
          style={{
            marginRight: "16px",
          }}
          onClick={() => {
            close(false);
            setSelectedEmployment(null);
            formik.resetForm();
          }}
        >
          Cancel
        </BaseButton>

        <BaseButton
          // Disabled by default; enabled only when there are valid unsaved changes.
          disabled={!formik.dirty || !formik.isValid}
          isLoading={
            isCreateEmploymentApiLoading || isUpdateEmploymentApiLoading
          }
          loaderSize={24}
          loaderColor="primary"
          className="px-[20px] py-[5px]"
          color="primary"
          onClick={formik.handleSubmit}
        >
          {isEdit ? "Update" : "Save Changes"}
        </BaseButton>
      </div>
    </BaseModal>
  );
};

export default AddEmpolymentHistoryModal;
